import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Search, SlidersHorizontal, X, Loader2 } from 'lucide-react';
import { Input }    from '@/components/ui/input';
import { Button }   from '@/components/ui/button';
import { Badge }    from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CourseCard from '../components/shared/CourseCard';

const API_BASE = 'https://xceededge.com'; // ← change to your path

export default function Courses() {
  const urlParams      = new URLSearchParams(window.location.search);
  const initialCategory = urlParams.get('category') || 'all';

  const [search,      setSearch]      = useState('');
  const [category,    setCategory]    = useState(initialCategory);
  const [level,       setLevel]       = useState('all');
  const [sort,        setSort]        = useState('popular');
  const [showFilters, setShowFilters] = useState(false);

  const [courses,    setCourses]    = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  // ── Fetch categories on mount ──────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API_BASE}/categories.php`)
      .then(res => {
        if (res.data.success) setCategories(res.data.data);
      })
      .catch(() => setError('Failed to load categories.'));
  }, []);

  // ── Fetch filtered courses whenever any filter changes ─────────────────────
  const fetchCourses = useCallback(() => {
    setLoading(true);
    setError('');

    const formData = new FormData();
    formData.append('action',   'filter');
    formData.append('search',   search);
    formData.append('category', category);
    formData.append('level',    level);
    formData.append('sort',     sort);

    axios.post(`${API_BASE}/courses.php`, formData)
      .then(res => {
        if (res.data.success) {
          setCourses(res.data.data);
        } else {
          setError('Failed to load courses.');
        }
      })
      .catch(() => setError('Server error. Please try again.'))
      .finally(() => setLoading(false));
  }, [search, category, level, sort]);

  // Debounce: wait 400ms after user stops typing before firing
  useEffect(() => {
    const timer = setTimeout(fetchCourses, 400);
    return () => clearTimeout(timer);
  }, [fetchCourses]);

  const activeFilters = [category !== 'all', level !== 'all', search].filter(Boolean).length;

  const clearAll = () => {
    setCategory('all');
    setLevel('all');
    setSearch('');
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        {/* Header */}
        <div className="mb-10">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold">
            Explore <span className="text-primary">Courses</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            {loading
              ? 'Loading...'
              : `Showing ${courses.length} course${courses.length !== 1 ? 's' : ''} across ${categories.length} categories`
            }
          </p>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Search & Sort */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search courses, skills, topics..."
              className="pl-10"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2 sm:w-auto"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {activeFilters > 0 && (
              <Badge className="bg-primary text-primary-foreground ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {activeFilters}
              </Badge>
            )}
          </Button>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="popular">Most Popular</SelectItem>
              <SelectItem value="latest">Highest Rated</SelectItem>
              <SelectItem value="price-low">Price: Low → High</SelectItem>
              <SelectItem value="price-high">Price: High → Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mb-8 p-5 bg-card rounded-xl border"
          >
            <div className="flex flex-wrap gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-2 block">Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-2 block">Level</label>
                <Select value={level} onValueChange={setLevel}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Levels</SelectItem>
                    <SelectItem value="Beginner">Beginner</SelectItem>
                    <SelectItem value="Intermediate">Intermediate</SelectItem>
                    <SelectItem value="Advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={clearAll}>
                  <X className="w-4 h-4 mr-1" /> Clear All
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Category Quick Nav */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setCategory('all')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
              category === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            All
          </button>
          {categories.map(c => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                category === c.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* Results count */}
        <p className="text-sm text-muted-foreground mb-6">
          {courses.length} course{courses.length !== 1 ? 's' : ''} found
        </p>

        {/* Course Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : courses.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {courses.map((course, i) => (
              <CourseCard key={course.id} course={course} index={i} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Search className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-lg">No courses found</h3>
            <p className="text-muted-foreground mt-1">Try adjusting your filters or search terms</p>
          </div>
        )}

      </div>
    </motion.div>
  );
}